import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, AlertTriangle, Lock, Trash2, MapPin, Plus, X, RotateCw } from "lucide-react";
import { useCartContext } from "../../../components/providers/CartProvider.jsx";
import { useAuthContext } from "../../../components/providers/AuthProvider.jsx";
import { BrandButton, ShippingEstimate } from "../../../components/ui/index.js";
import {
    DireccionFormFields,
    describirDireccion,
    direccionToCheckout,
    parseErroresDireccion,
    useDirecciones,
    useDireccionForm,
} from "../../../domain/direcciones/index.js";

const fmt = (n, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(n || 0));

const inputCls =
    "input input-bordered w-full bg-base-100/40 dark:bg-base-100/20 " +
    "border-white/15 focus:border-brand-red/50 focus:outline-none transition-colors";

const initialFormState = {
    contact_email: "",
    contact_phone: "",
    shipping_name: "",
    shipping_address_line1: "",
    shipping_address_line2: "",
    shipping_city: "",
    shipping_state: "",
    shipping_postal_code: "",
    shipping_country: "España",
    notes: "",
    accepted_general_terms: false,
    accepted_digital_license: false,
};

// Siempre obligatorios, sea cual sea la clasificación (MVP-02 §4: digital-only
// sigue necesitando identidad/contacto).
const REQUIRED_FIELDS = ["contact_email"];

// Solo obligatorios cuando el carrito tiene alguna línea física — coincide
// exactamente con `checkout_service.REQUIRED_ADDRESS_FIELDS` del backend.
const PHYSICAL_REQUIRED_FIELDS = [
    "shipping_name",
    "shipping_address_line1",
    "shipping_city",
    "shipping_postal_code",
];

const SHIPPING_FIELD_LABELS = {
    shipping_name: "nombre de contacto",
    shipping_address_line1: "calle y número",
    shipping_city: "municipio",
    shipping_postal_code: "código postal",
};

const isFixedQuantity = (item) => item?.item_type === "custom_order" || item?.item_type === "digital";

const CheckoutPage = () => {
    const navigate = useNavigate();
    const { cartItems, fetchCart } = useCartContext();
    const authContext = useAuthContext();

    const [form, setForm] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [invalidItems, setInvalidItems] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [addressSaving, setAddressSaving] = useState(false);

    // Direcciones y geografía se cargan por separado (MVP-02B §3.1). Cuando
    // viajaban juntas en un `Promise.all`, un fallo de `/api/direcciones/`
    // dejaba también vacía la lista de provincias y el mensaje solo hablaba de
    // direcciones, lo que desviaba el diagnóstico del fallo real.
    const { direcciones, predeterminada, recargar: recargarDirecciones, crear: crearDireccion } =
        useDirecciones({ enabled: Boolean(authContext?.isAuth) });
    const addressForm = useDireccionForm();

    // Cotización de envío server-side (MVP-02A). El importe, el peso y el
    // servicio los fija el backend; aquí solo se presenta lo que devuelve.
    const [shipping, setShipping] = useState({ state: "idle", data: null, message: "" });
    const [checkoutSummary, setCheckoutSummary] = useState(null);
    const [quoteNonce, setQuoteNonce] = useState(0);

    // Versiones legales activas (MVP-02 §7): referencia de versión, nunca el
    // texto legal en sí. Si falta la que el carrito necesita, el checkout se
    // bloquea de forma explícita en vez de dejar fallar solo al enviar.
    const [legalVersions, setLegalVersions] = useState({ general_terms: null, digital_license: null, loaded: false });

    // Clave de idempotencia (MVP-02 §6): protege un doble clic/reintento del
    // mismo intento de pago. Se regenera si el carrito cambia de verdad,
    // porque eso ya es, por definición, un intento distinto.
    const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

    // Prellenar con los datos del perfil (best-effort)
    useEffect(() => {
        if (!authContext?.isAuth) return;
        let cancelled = false;
        axios.get("/data_profile_user/")
            .then(res => {
                if (cancelled) return;
                const u = res.data || {};
                setForm(prev => ({
                    ...prev,
                    contact_email: u.email || prev.contact_email,
                }));
                addressForm.setValue(prev => ({
                    ...prev,
                    nombre_contacto: prev.nombre_contacto || u.name || "",
                    telefono: prev.telefono || u.phone || "",
                }));
            })
            .catch(() => { /* prellenado no crítico */ });
        return () => { cancelled = true; };
        // `addressForm.setValue` es estable (useCallback en el hook); incluirlo
        // reengancharía el efecto en cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authContext?.isAuth]);

    // Preselecciona la predeterminada en cuanto llega la lista. Si no hay
    // ninguna guardada, se abre directamente el formulario de alta.
    useEffect(() => {
        if (direcciones.state === "empty") {
            setShowAddressForm(true);
            return;
        }
        if (direcciones.state !== "ready" || selectedAddressId) return;
        if (predeterminada) {
            setSelectedAddressId(predeterminada.id);
            setForm(prev => ({ ...prev, ...direccionToCheckout(predeterminada) }));
        }
    }, [direcciones.state, predeterminada, selectedAddressId]);

    // Refrescar carrito al montar (por si se llega tras login)
    useEffect(() => { fetchCart?.(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // Versiones legales activas: se cargan una vez, públicas.
    useEffect(() => {
        let cancelled = false;
        axios.get("/legal/active/")
            .then(({ data }) => {
                if (cancelled) return;
                setLegalVersions({ ...data, loaded: true });
            })
            .catch(() => { if (!cancelled) setLegalVersions(prev => ({ ...prev, loaded: true })); });
        return () => { cancelled = true; };
    }, []);

    const hasCustomItems = useMemo(
        () => (cartItems || []).some(i => i?.item_type === "custom_order"),
        [cartItems]
    );
    const hasDigitalItems = useMemo(
        () => (cartItems || []).some(i => i?.item_type === "digital"),
        [cartItems]
    );
    const hasPhysicalItems = useMemo(
        () => (cartItems || []).some(i => i?.item_type === "product" || i?.item_type === "custom_order"),
        [cartItems]
    );
    // Clasificación local, solo para adaptar la UI (mostrar/ocultar
    // dirección). La autoridad real es siempre el backend, que la deriva de
    // nuevo de las líneas del carrito al crear el pedido (MVP-02 §4).
    const isDigitalOnly = (cartItems?.length ?? 0) > 0 && hasDigitalItems && !hasPhysicalItems;

    const localSubtotal = useMemo(() => (cartItems || []).reduce((acc, item) => {
        if (isFixedQuantity(item)) {
            return acc + (parseFloat(item?.product_price) || 0);
        }
        return acc + (parseFloat(item?.product_price) || 0) * (item?.quantity || 0);
    }, 0), [cartItems]);

    const localCurrency = useMemo(() => {
        const first = (cartItems || []).find(i => isFixedQuantity(i));
        return first?.snapshot_currency || "EUR";
    }, [cartItems]);

    const isEmpty = !cartItems || cartItems.length === 0;

    // Firma del carrito: cambiar artículos o cantidades invalida la estimación
    // y fuerza recalcular (coherente con la invalidación server-side). Cambiar
    // de carrito también obliga a una nueva clave de idempotencia: es, por
    // definición, un intento de pago distinto.
    const cartSignature = useMemo(
        () => (cartItems || []).map(i => `${i.id}:${i.quantity}`).join("|"),
        [cartItems]
    );
    useEffect(() => {
        setIdempotencyKey(crypto.randomUUID());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartSignature]);

    // Recalcula el envío cuando cambian el destino o el carrito.
    // Digital-only nunca cotiza: coste cero como consecuencia explícita del
    // tipo, no como fallback (MVP-02 §4), y sin pedir CP porque no hay
    // formulario de dirección que rellenar.
    useEffect(() => {
        if (isEmpty) {
            setShipping({ state: "idle", data: null, message: "" });
            setCheckoutSummary(null);
            return;
        }
        const cp = (form.shipping_postal_code || "").replace(/\D/g, "");
        const missingAddressFields = PHYSICAL_REQUIRED_FIELDS.filter(
            field => !String(form[field] || "").trim(),
        );
        if (!isDigitalOnly && (cp.length !== 5 || missingAddressFields.length > 0)) {
            const selectedAddressIsIncomplete = Boolean(selectedAddressId) && missingAddressFields.length > 0;
            setShipping({
                state: selectedAddressIsIncomplete ? "incomplete" : "idle",
                data: null,
                message: selectedAddressIsIncomplete
                    ? `Completa ${missingAddressFields.map(field => SHIPPING_FIELD_LABELS[field]).join(", ")} en la dirección seleccionada.`
                    : "",
            });
            setCheckoutSummary(null);
            return;
        }
        let cancelled = false;
        setShipping(prev => ({ ...prev, state: "loading" }));
        setCheckoutSummary(null);
        const t = setTimeout(async () => {
            try {
                const { data } = await axios.post(
                    "/orders/checkout/summary/",
                    {
                        shipping_name: form.shipping_name || "",
                        shipping_address_line1: form.shipping_address_line1 || "",
                        shipping_city: form.shipping_city || "",
                        shipping_postal_code: cp,
                        shipping_country: form.shipping_country || "España",
                        shipping_state: form.shipping_state || "",
                    },
                    { withCredentials: true }
                );
                if (cancelled) return;
                const quote = data?.shipping;
                setCheckoutSummary(data);
                if (!quote?.shipping_available) {
                    setShipping({ state: "unavailable", data: null, message: quote?.detail || "" });
                } else if (quote.is_digital_only) {
                    setShipping({ state: "digital", data: quote, message: "" });
                } else {
                    setShipping({ state: "available", data: quote, message: "" });
                }
            } catch (err) {
                if (cancelled) return;
                const st = err.response?.status;
                if (st === 401 || st === 403) navigate("/login");
                const detail = err.response?.data?.detail;
                setCheckoutSummary(null);
                setShipping({
                    state: st === 422 ? "unavailable" : "error",
                    data: null,
                    message: detail || "No se pudo calcular el resumen del pedido.",
                });
            }
        }, 350);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        form.shipping_name,
        form.shipping_address_line1,
        form.shipping_city,
        form.shipping_postal_code,
        form.shipping_country,
        form.shipping_state,
        cartSignature,
        quoteNonce,
        isDigitalOnly,
        isEmpty,
        selectedAddressId,
    ]);

    const subtotal = checkoutSummary ? (parseFloat(checkoutSummary.subtotal) || 0) : localSubtotal;
    const currency = checkoutSummary?.currency || localCurrency;
    const shippingCost = checkoutSummary
        ? (parseFloat(checkoutSummary.shipping?.shipping_cost) || 0)
        : 0;
    const shippingReady = shipping.state === "available" || shipping.state === "digital";
    const total = checkoutSummary
        ? (parseFloat(checkoutSummary.total) || 0)
        : subtotal + shippingCost;
    const testerPricingApplied = Boolean(
        checkoutSummary?.lines?.some(line => line.tester_price_applied)
    );

    // No se puede confirmar sin la(s) versión(es) legal(es) que este carrito
    // necesita configuradas en el entorno (MVP-02 §7).
    const legalReady = legalVersions.loaded
        && Boolean(legalVersions.general_terms)
        && (!hasDigitalItems || Boolean(legalVersions.digital_license));

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const selectAddress = (address) => {
        setSelectedAddressId(address.id);
        setForm(prev => ({ ...prev, ...direccionToCheckout(address) }));
        setErrors(prev => ({
            ...prev,
            contact_phone: undefined,
            shipping_name: undefined,
            shipping_address_line1: undefined,
            shipping_address_line2: undefined,
            shipping_city: undefined,
            shipping_state: undefined,
            shipping_postal_code: undefined,
            shipping_country: undefined,
        }));
    };

    /**
     * Crea la dirección y la usa inmediatamente (MVP-02B §3.8).
     *
     * El servidor devuelve la dirección ya resuelta (ids + nombres), así que no
     * hay que reconstruir la localidad buscando en listas locales, que era como
     * antes se colaba una localidad vacía al checkout si la lista de municipios
     * todavía no había llegado.
     */
    const saveNewAddress = async () => {
        if (!addressForm.validar()) return;
        setAddressSaving(true);
        try {
            const creada = await crearDireccion(addressForm.value);
            selectAddress(creada);
            addressForm.reset();
            setShowAddressForm(false);
        } catch (error) {
            addressForm.setErrors(parseErroresDireccion(error));
        } finally {
            setAddressSaving(false);
        }
    };

    const validate = () => {
        const next = {};
        const fieldsToCheck = isDigitalOnly ? REQUIRED_FIELDS : [...REQUIRED_FIELDS, ...PHYSICAL_REQUIRED_FIELDS];
        if (!isDigitalOnly && !selectedAddressId) {
            next.shipping_address_line1 = "Selecciona o añade una dirección de envío";
        }
        fieldsToCheck.forEach(f => {
            if (!form[f] || !String(form[f]).trim()) next[f] = "Campo obligatorio";
        });
        if (form.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email)) {
            next.contact_email = "Email no válido";
        }
        if (!form.accepted_general_terms) {
            next.accepted_general_terms = "Debes aceptar las condiciones generales.";
        }
        if (hasDigitalItems && !form.accepted_digital_license) {
            next.accepted_digital_license = "Debes aceptar la licencia del paquete digital.";
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError(null);
        setInvalidItems([]);
        if (isEmpty) {
            setServerError("Tu carrito está vacío. Añade productos antes de finalizar el pedido.");
            return;
        }
        if (!validate()) return;

        setSubmitting(true);
        try {
            const { data } = await axios.post("/orders/checkout/", form, {
                withCredentials: true,
                headers: { "Idempotency-Key": idempotencyKey },
            });
            await fetchCart?.();
            navigate(`/checkout/success/${data.order_number}`, { replace: true, state: { order: data } });
        } catch (err) {
            const status = err.response?.status;
            const data = err.response?.data;
            if (status === 409 && data?.invalid_items) {
                setInvalidItems(data.invalid_items);
                setServerError(data.detail || "Hay líneas en tu carrito que ya no son válidas.");
            } else if (status === 409 && data?.code === "idempotency_conflict") {
                setServerError("Este intento de pago ya se procesó con otros datos. Recarga la página e inténtalo de nuevo.");
            } else if (status === 422 && data?.code === "legal_version_missing") {
                setServerError(
                    "Este entorno todavía no tiene configurada la versión de condiciones necesaria para vender. "
                    + "No es posible completar el pedido ahora mismo."
                );
            } else if (status === 422) {
                // Envío no disponible al confirmar (CP no cubierto, sin tarifa
                // vigente o sin embalaje compatible): no se crea el pedido.
                setShipping({ state: "unavailable", data: null, message: data?.detail || "" });
                setServerError(data?.detail || "El envío no está disponible para este pedido.");
            } else if (status === 400) {
                setServerError(data?.detail || "Revisa los datos del formulario.");
                const fieldErrors = data?.errors || data;
                if (fieldErrors && typeof fieldErrors === "object") {
                    const normalized = Object.fromEntries(
                        Object.entries(fieldErrors).map(([key, value]) => [
                            key, Array.isArray(value) ? value.join(" ") : value,
                        ])
                    );
                    setErrors(prev => ({ ...prev, ...normalized }));
                }
            } else if (status === 401 || status === 403) {
                navigate("/login");
            } else {
                setServerError(data?.detail || "No se pudo crear el pedido. Inténtalo de nuevo.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="h-full w-full overflow-y-scroll" style={{ minHeight: 0 }}>
            <div className="px-4 md:px-10 lg:px-20 py-6 flex flex-col gap-6 max-w-6xl mx-auto">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold dark:text-white text-gray-900">
                        Finalizar pedido
                    </h1>
                </div>

                {isEmpty ? (
                    <motion.div className="flex flex-col items-center justify-center gap-5 py-24"
                                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                        <ShoppingBag size={56} className="text-gray-300 dark:text-gray-600" />
                        <div className="text-center">
                            <h2 className="text-xl font-semibold mb-1">Tu carrito está vacío</h2>
                            <p className="text-gray-400 text-sm">Añade productos antes de proceder al checkout.</p>
                        </div>
                        <BrandButton as="link" to="/store" size="md">Ir a la tienda</BrandButton>
                    </motion.div>
                ) : (
                    <form className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" onSubmit={handleSubmit} noValidate>
                        {/* Formulario */}
                        <div className="lg:col-span-2 glassCard p-5 md:p-6 rounded-2xl flex flex-col gap-6">
                            {hasCustomItems && (
                                <div role="alert" className="alert alert-info text-sm">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>
                                        Tu pedido incluye un diseño personalizado aprobado. El equipo se pondrá en contacto
                                        para coordinar el pago y la producción.
                                    </span>
                                </div>
                            )}

                            {isDigitalOnly && (
                                <div role="status" className="alert alert-info text-sm">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>
                                        Este pedido es solo digital: no necesita dirección de envío ni tiene coste de transporte.
                                        El archivo se habilitará para descarga después de confirmar el pago, no al crear el pedido.
                                    </span>
                                </div>
                            )}

                            <section>
                                <h2 className="text-lg font-semibold mb-3">Contacto</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Email *" name="contact_email" type="email"
                                           value={form.contact_email} onChange={handleChange} error={errors.contact_email} />
                                    <Field label="Teléfono" name="contact_phone"
                                           value={form.contact_phone} onChange={handleChange} error={errors.contact_phone} />
                                </div>
                            </section>

                            {!isDigitalOnly && (
                            <section>
                                <h2 className="text-lg font-semibold mb-3">Dirección de envío</h2>
                                {errors.shipping_address_line1 && !selectedAddressId && (
                                    <div className="alert alert-error py-2 mb-3 text-sm">
                                        <AlertTriangle className="h-4 w-4" />
                                        {errors.shipping_address_line1}
                                    </div>
                                )}
                                {direcciones.state === "error" && (
                                    <div
                                        role="alert"
                                        className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm"
                                    >
                                        <span className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-warning" />
                                            No se pudieron cargar tus direcciones guardadas. Puedes
                                            reintentarlo o añadir una nueva.
                                        </span>
                                        <button
                                            type="button"
                                            onClick={recargarDirecciones}
                                            className="btn btn-ghost btn-xs gap-1"
                                        >
                                            <RotateCw className="h-3 w-3" />
                                            Reintentar
                                        </button>
                                    </div>
                                )}
                                {direcciones.state === "loading" ? (
                                    <div className="flex items-center gap-2 py-4 text-sm text-base-content/60">
                                        <span className="loading loading-spinner loading-sm" /> Cargando direcciones…
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {direcciones.data.map(address => (
                                            <button
                                                key={address.id}
                                                type="button"
                                                onClick={() => selectAddress(address)}
                                                className={`rounded-xl border p-4 text-left transition-colors ${selectedAddressId === address.id
                                                    ? "border-primary bg-primary/10 ring-1 ring-primary"
                                                    : "border-base-300 bg-base-200/40 hover:border-primary/50"}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                                                    <div className="min-w-0 flex-1 text-sm">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span className="font-semibold">{address.nombre_contacto}</span>
                                                            {address.predeterminado && <span className="badge badge-primary badge-sm">Predeterminada</span>}
                                                        </div>
                                                        <p>{address.direccion}{address.apartamento ? `, ${address.apartamento}` : ""}</p>
                                                        <p className="text-base-content/65">
                                                            {describirDireccion(address)}
                                                        </p>
                                                        <p className="text-base-content/65">{address.pais} · {address.telefono}</p>
                                                    </div>
                                                </div>
                                            </button>
                                        ))}

                                        <button
                                            type="button"
                                            className="btn btn-outline btn-sm self-start"
                                            onClick={() => setShowAddressForm(value => !value)}
                                        >
                                            {showAddressForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                                            {showAddressForm ? "Cancelar" : "Añadir otra dirección"}
                                        </button>

                                        {showAddressForm && (
                                            <div className="rounded-xl border border-base-300 bg-base-200/40 p-4 flex flex-col gap-4">
                                                <div>
                                                    <h3 className="font-semibold">Nueva dirección</h3>
                                                    <p className="text-xs text-base-content/60">
                                                        Se guardará también en tu perfil.
                                                    </p>
                                                </div>
                                                <DireccionFormFields
                                                    form={addressForm}
                                                    variant="checkout"
                                                    disabled={addressSaving}
                                                />
                                                <BrandButton
                                                    type="button"
                                                    onClick={saveNewAddress}
                                                    disabled={addressSaving}
                                                    className="self-start"
                                                >
                                                    {addressSaving ? "Guardando…" : "Guardar y usar esta dirección"}
                                                </BrandButton>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="sr-only" aria-live="polite">
                                    {selectedAddressId ? "Dirección de envío seleccionada" : "Selecciona o añade una dirección"}
                                </div>
                            </section>
                            )}

                            <section>
                                <h2 className="text-lg font-semibold mb-3">Notas (opcional)</h2>
                                <textarea
                                    name="notes" rows={3} value={form.notes} onChange={handleChange}
                                    className={inputCls + " resize-none"}
                                    placeholder="Instrucciones especiales, referencias, etc."
                                />
                            </section>

                            <section>
                                <h2 className="text-lg font-semibold mb-3">Condiciones</h2>
                                <div className="flex flex-col gap-3">
                                    <LegalCheckbox
                                        name="accepted_general_terms"
                                        label="Condiciones generales de compra"
                                        version={legalVersions.general_terms}
                                        loaded={legalVersions.loaded}
                                        checked={form.accepted_general_terms}
                                        onChange={handleChange}
                                        error={errors.accepted_general_terms}
                                    />
                                    {hasDigitalItems && (
                                        <LegalCheckbox
                                            name="accepted_digital_license"
                                            label="Licencia de uso del paquete digital"
                                            version={legalVersions.digital_license}
                                            loaded={legalVersions.loaded}
                                            checked={form.accepted_digital_license}
                                            onChange={handleChange}
                                            error={errors.accepted_digital_license}
                                        />
                                    )}
                                </div>
                            </section>

                            {serverError && (
                                <div role="alert" className="alert alert-error text-sm">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span>{serverError}</span>
                                </div>
                            )}
                            {invalidItems.length > 0 && (
                                <div className="bg-error/15 border border-error/40 rounded-lg p-4 flex flex-col gap-3">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-error text-sm">
                                                Hay {invalidItems.length} {invalidItems.length === 1 ? "línea" : "líneas"} en tu carrito que ya no {invalidItems.length === 1 ? "es válida" : "son válidas"}
                                            </p>
                                            <p className="text-xs text-error/80 mt-1">
                                                Puede ser una propuesta que cambió de estado, un paquete digital que dejó de estar aprobado
                                                o un producto sin stock suficiente. Elimínalas o ajústalas en el carrito para continuar.
                                            </p>
                                        </div>
                                    </div>
                                    <ul className="flex flex-col gap-2">
                                        {invalidItems.map(item => (
                                            <li key={item.item_id} className="bg-base-300/30 rounded p-2 text-xs text-error">
                                                <p className="font-medium">{item.snapshot_name || "Artículo"}</p>
                                                <p className="text-error/90 mt-0.5">{item.reason}</p>
                                            </li>
                                        ))}
                                    </ul>
                                    <BrandButton as="link" to="/cart" size="sm" variant="ghost" className="w-full mt-1 text-error">
                                        <Trash2 className="w-4 h-4" /> Ir al carrito para eliminarlas
                                    </BrandButton>
                                </div>
                            )}
                        </div>

                        {/* Resumen */}
                        <aside className="glassCard p-5 rounded-2xl flex flex-col gap-4 sticky top-4">
                            <h2 className="text-lg font-bold">Resumen</h2>
                            <ul className="flex flex-col gap-2 text-sm">
                                {cartItems.map(item => {
                                    const summaryLine = checkoutSummary?.lines?.find(line => line.id === item.id);
                                    const regularLineTotal = summaryLine
                                        ? (parseFloat(summaryLine.regular_line_total) || 0)
                                        : (isFixedQuantity(item)
                                            ? (parseFloat(item.product_price) || 0)
                                            : (parseFloat(item.product_price) || 0) * item.quantity);
                                    const displayLineTotal = summaryLine
                                        ? (parseFloat(summaryLine.line_total) || 0)
                                        : regularLineTotal;
                                    return (
                                    <li key={item.id} className="flex justify-between gap-3 items-center">
                                        <div className="flex items-center gap-2 truncate flex-1">
                                            <span className="truncate">
                                                {item.item_type === "custom_order"
                                                    ? <>1× {item.product_name} <span className="text-xs text-base-content/60">(lote {item.quantity})</span></>
                                                    : item.item_type === "digital"
                                                        ? <>1× {item.product_name} <span className="text-xs text-base-content/60">(digital)</span></>
                                                        : <>{item.quantity}× {item.product_name}</>}
                                            </span>
                                            {isFixedQuantity(item) && (
                                                <span className="badge badge-xs bg-blue-500/20 text-blue-300 border-blue-500/30 whitespace-nowrap flex-shrink-0 tooltip"
                                                      data-tip="Precio congelado">
                                                    <Lock className="w-3 h-3" />
                                                </span>
                                            )}
                                        </div>
                                        <span className="whitespace-nowrap font-medium text-right">
                                            {summaryLine?.tester_price_applied && (
                                                <span className="block text-xs line-through text-base-content/50">
                                                    {fmt(regularLineTotal, item.snapshot_currency || currency)}
                                                </span>
                                            )}
                                            {summaryLine?.tester_price_applied ? "Gratis tester" : fmt(displayLineTotal, item.snapshot_currency || currency)}
                                        </span>
                                    </li>
                                    );
                                })}
                                {hasDigitalItems && (
                                    <li className="text-xs text-base-content/60 -mt-1">
                                        {testerPricingApplied
                                            ? "La línea digital tester queda habilitada al crear el pedido y no genera un pago."
                                            : "El paquete digital se habilita tras confirmar el pago, no al crear el pedido."}
                                    </li>
                                )}
                            </ul>
                            <div className="flex flex-col gap-1 text-sm border-t border-white/10 pt-3">
                                <div className="flex justify-between text-base-content/70">
                                    <span>Subtotal</span><span>{fmt(subtotal, currency)}</span>
                                </div>
                                <ShippingEstimate
                                    state={shipping.state}
                                    cost={shipping.data?.shipping_cost}
                                    currency={shipping.data?.currency || currency}
                                    deliveryMin={shipping.data?.delivery_interval?.min_date}
                                    deliveryMax={shipping.data?.delivery_interval?.max_date}
                                    message={shipping.message}
                                    onRetry={() => setQuoteNonce(n => n + 1)}
                                />
                            </div>
                            <div className="flex justify-between border-t border-white/10 pt-3">
                                <span className="font-bold text-lg">Total</span>
                                <span className="font-bold text-xl">
                                    {shippingReady ? fmt(total, currency) : fmt(subtotal, currency)}
                                    {shipping.state === "available" && shippingCost > 0 && (
                                        <span className="block text-[10px] font-normal text-base-content/50 text-right">
                                            incl. envío {fmt(shippingCost, currency)}
                                        </span>
                                    )}
                                </span>
                            </div>

                            <BrandButton type="submit" variant="solid" size="md"
                                         className="w-full justify-center"
                                         disabled={submitting || isEmpty || !shippingReady || !legalReady}>
                                {submitting ? "Procesando…" : "Confirmar pedido"}
                            </BrandButton>
                            {!shippingReady && !isEmpty && (
                                <p className="text-center text-xs text-base-content/50">
                                    {shipping.state === "loading"
                                        ? "Calculando el envío…"
                                        : "Calcula un envío disponible para confirmar el pedido."}
                                </p>
                            )}
                            {shippingReady && !legalReady && (
                                <p className="text-center text-xs text-warning">
                                    Faltan versiones legales configuradas en este entorno para poder vender.
                                </p>
                            )}
                            <BrandButton as="link" to="/cart" variant="ghost" size="sm" className="w-full justify-center">
                                ← Volver al carrito
                            </BrandButton>
                            <p className="text-center text-xs text-base-content/50">
                                Sin pasarela de pago todavía: tu pedido quedará pendiente y nuestro equipo te contactará.
                            </p>
                        </aside>
                    </form>
                )}
            </div>
        </div>
    );
};

const Field = ({ label, name, type = "text", value, onChange, error }) => (
    <div>
        <label className="block text-xs font-medium mb-1 text-base-content/70">{label}</label>
        <input
            type={type} name={name} value={value} onChange={onChange}
            className={inputCls + (error ? " border-error focus:border-error" : "")}
        />
        {error && <p className="text-error text-xs mt-1">{error}</p>}
    </div>
);

const LegalCheckbox = ({ name, label, version, loaded, checked, onChange, error }) => {
    const missing = loaded && !version;
    return (
        <div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                    type="checkbox"
                    name={name}
                    checked={checked}
                    onChange={onChange}
                    disabled={missing}
                    className={"checkbox checkbox-sm mt-0.5" + (error ? " checkbox-error" : " checkbox-primary")}
                />
                <span>
                    {label}
                    {version && (
                        <span className="text-base-content/50"> (versión {version.version_label})</span>
                    )}
                </span>
            </label>
            {version?.reference_url && (
                <a
                    href={version.reference_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link link-primary text-xs mt-1 ml-6 inline-block"
                >
                    Leer el documento
                </a>
            )}
            {missing && (
                <p className="text-warning text-xs mt-1 ml-6">
                    Esta versión todavía no está configurada en este entorno; no se puede aceptar todavía.
                </p>
            )}
            {error && <p className="text-error text-xs mt-1 ml-6">{error}</p>}
        </div>
    );
};


Field.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
};

LegalCheckbox.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    version: PropTypes.shape({
        version_label: PropTypes.string,
        reference_url: PropTypes.string,
    }),
    loaded: PropTypes.bool.isRequired,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
};


export default CheckoutPage;
